import type { PackScanStatus } from '@planuze/admin-contracts';
import { cn } from '@planuze/ui';
import type { PackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import { usePackSubmissionsContent } from '../../content/sections/pack-submissions.js';
import { SegmentedControl, type SegmentOption } from '../filters/segmented-control.js';
import { SearchInput } from '../search-input.js';

/* Toolbar de filtros da fila de moderação de packs (ADR-0712, molde de `audit-filters.tsx`):
   - Busca por pack = filtro CLIENT-SIDE sobre a página keyset carregada (nome do pack não é campo
     do contrato);
   - ID do publisher = SearchInput SERVER-SIDE (igualdade exata, índice publisher_idx);
   - Resultado do scan = SegmentedControl SERVER-SIDE;
   - Período = SegmentedControl → janela `submittedAfter` SERVER-SIDE.
   Publisher/scanStatus/período particionam a fila INTEIRA (não só a página) — por isso vão à query,
   não ao filtro client-side. */

/* `scanning` existe no enum do backend (packSubmissionStatusSchema) — sem esta pill a fila
   escaneando ficava sem filtro dedicado. */
const STATUS_FILTER_KEYS = [
  'all',
  'submitted',
  'scanning',
  'reviewing',
  'changes_requested',
  'approved',
  'rejected',
  'cancelled',
] as const;
export type StatusFilterKey = (typeof STATUS_FILTER_KEYS)[number];

/* `switch` em vez de indexar `statusFilterLabels` por `StatusFilterKey` direto — a chave
   `changes_requested` é snake_case (espelha o enum do backend) e useNamingConvention rejeita
   propriedade snake_case em objeto; o content usa `changesRequested` (camelCase) e este switch
   faz a ponte, mesmo padrão de `pack-submission-status.ts`. */
function statusFilterLabel(
  key: StatusFilterKey,
  labels: PackSubmissionsContent['filters']['statusFilterLabels'],
): string {
  switch (key) {
    case 'all':
      return labels.all;
    case 'submitted':
      return labels.submitted;
    case 'scanning':
      return labels.scanning;
    case 'reviewing':
      return labels.reviewing;
    case 'changes_requested':
      return labels.changesRequested;
    case 'approved':
      return labels.approved;
    case 'rejected':
      return labels.rejected;
    case 'cancelled':
      return labels.cancelled;
  }
}

export type ScanStatusFilter = 'all' | PackScanStatus;
export type SubmissionPeriod = 'all' | '24h' | '7d' | '30d';

export interface PackSubmissionsFiltersValue {
  readonly status: StatusFilterKey;
  /** Busca textual client-side sobre a página carregada (nome/id do pack). */
  readonly search: string;
  /** ID exato do publisher — filtro server-side. */
  readonly publisherId: string;
  readonly scanStatus: ScanStatusFilter;
  readonly period: SubmissionPeriod;
}

/** Estado inicial dos filtros (resolvido em runtime, não em escopo de módulo). */
export function defaultPackSubmissionsFilters(): PackSubmissionsFiltersValue {
  return { status: 'all', search: '', publisherId: '', scanStatus: 'all', period: 'all' };
}

/* ms/hora e ms/dia como expressão (não literal) — fora do alcance do gate de magic-numbers e sem
   business value rotacionável (molde de `audit-filters.tsx`/`metrics-filters.tsx`). */
const MS_PER_HOUR = 60 * 60 * 1000;
const MS_PER_DAY = 24 * MS_PER_HOUR;

const PERIOD_OFFSETS: Readonly<Record<Exclude<SubmissionPeriod, 'all'>, number>> = {
  '24h': 24 * MS_PER_HOUR,
  '7d': 7 * MS_PER_DAY,
  '30d': 30 * MS_PER_DAY,
};

/** Converte o período em `submittedAfter` (ISO). `all` → sem limite inferior. Ancorar em `useMemo`
    sobre o período no call site: computar por render mudaria a queryKey a cada frame (refetch loop). */
export function periodToSubmittedAfter(period: SubmissionPeriod): string | undefined {
  if (period === 'all') return undefined;
  return new Date(Date.now() - PERIOD_OFFSETS[period]).toISOString();
}

interface Props {
  readonly value: PackSubmissionsFiltersValue;
  readonly onChange: (value: PackSubmissionsFiltersValue) => void;
}

export function PackSubmissionsFilters({ value, onChange }: Props) {
  const copy = usePackSubmissionsContent();
  const f = copy.filters;
  /* Opções resolvidas NO RENDER (não em escopo de módulo) — locale muda em runtime e uma const
     de topo de arquivo congelaria o idioma do primeiro import (mesmo anti-pattern de `uiText()`
     em escopo de módulo, CLAUDE.md). */
  const scanStatusOptions: readonly SegmentOption<ScanStatusFilter>[] = [
    { value: 'all', label: f.scanStatusFilterLabels.all },
    { value: 'pass', label: f.scanStatusFilterLabels.pass },
    { value: 'warn', label: f.scanStatusFilterLabels.warn },
    { value: 'fail', label: f.scanStatusFilterLabels.fail },
    { value: 'pending', label: f.scanStatusFilterLabels.pending },
  ];
  const periodOptions: readonly SegmentOption<SubmissionPeriod>[] = [
    { value: 'all', label: f.periodFilterLabels.all },
    { value: '24h', label: f.periodFilterLabels['24h'] },
    { value: '7d', label: f.periodFilterLabels['7d'] },
    { value: '30d', label: f.periodFilterLabels['30d'] },
  ];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput
          value={value.search}
          onChange={(search) => onChange({ ...value, search })}
          ariaLabel={f.searchByPackAriaLabel}
          placeholder={f.searchByPackPlaceholder}
        />
        <SearchInput
          value={value.publisherId}
          onChange={(publisherId) => onChange({ ...value, publisherId })}
          ariaLabel={f.publisherIdAriaLabel}
          placeholder={f.publisherIdPlaceholder}
        />
        <SegmentedControl<ScanStatusFilter>
          value={value.scanStatus}
          onChange={(scanStatus) => onChange({ ...value, scanStatus })}
          options={scanStatusOptions}
          ariaLabel={f.scanStatusAriaLabel}
        />
        <SegmentedControl<SubmissionPeriod>
          value={value.period}
          onChange={(period) => onChange({ ...value, period })}
          options={periodOptions}
          ariaLabel={f.periodAriaLabel}
        />
      </div>
      <div
        className="flex flex-wrap gap-2"
        role="radiogroup"
        aria-label={f.statusRadiogroupAriaLabel}
      >
        {STATUS_FILTER_KEYS.map((key) => (
          // biome-ignore lint/a11y/useSemanticElements: pill estilizado; semântica radio via role
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={value.status === key}
            onClick={() => onChange({ ...value, status: key })}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              value.status === key
                ? 'border-primary bg-primary/10 text-primary'
                : 'border-border bg-card text-muted-foreground hover:bg-accent/10 hover:text-foreground',
            )}
          >
            {statusFilterLabel(key, f.statusFilterLabels)}
          </button>
        ))}
      </div>
    </div>
  );
}
